import React, { useState } from 'react';
import Papa from 'papaparse';
import { supabase } from '../../../lib/supabaseClient';

const POSProcessor = () => {
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [feedback, setFeedback] = useState('');
    const [error, setError] = useState('');

    const uploadCSVToSupabase = async (file) => {
        setProcessing(true);
        setFeedback('');
        setError('');

        Papa.parse(file, {
            header: true,
            dynamicTyping: true, // Automatically convert numbers
            skipEmptyLines: true, // Skip empty lines
            complete: async (results) => {
                const totalEntries = results.data.length;
                setProgress({ current: 0, total: totalEntries });

                const processedData = results.data.map((row) => ({
                    fiche_agence: row.fiche_agence,
                    titre_affiche: row.titre_affiche,
                    adresse: row.adresse,
                    design_points_of_sales: row.design_points_of_sales,
                    photos_projet: row.photos_projet,
                    longitude: parseFloat(row.longitude),
                    latitude: parseFloat(row.lattitude) // Note: handles the typo in CSV
                }));

                // Filter out any rows with invalid data
                const validData = processedData.filter(row => 
                    row.longitude && 
                    row.latitude && 
                    !isNaN(row.longitude) && 
                    !isNaN(row.latitude)
                );

                try {
                    // Insert in batches of 100 to avoid payload size limits
                    const batchSize = 100;
                    for (let i = 0; i < validData.length; i += batchSize) {
                        const batch = validData.slice(i, i + batchSize);
                        const { data, error: supabaseError } = await supabase
                            .from('points_of_sale')
                            .insert(batch);

                        if (supabaseError) {
                            throw supabaseError;
                        }

                        // Update progress
                        setProgress({ 
                            current: Math.min(i + batchSize, validData.length), 
                            total: validData.length 
                        });
                    }

                    setFeedback(`Successfully uploaded ${validData.length} entries.`);
                } catch (uploadError) {
                    console.error('Upload error:', uploadError);
                    setError(`Error uploading data: ${uploadError.message}`);
                } finally {
                    setProcessing(false);
                }
            },
            error: (parseError) => {
                console.error('CSV parsing error:', parseError);
                setError(`CSV parsing error: ${parseError.message}`);
                setProcessing(false);
            },
        });
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            uploadCSVToSupabase(file);
        }
    };

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
            <h2>Process Points of Sale Data</h2>

            <div style={{
                padding: '20px',
                marginBottom: '20px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: '#f5f5f5',
            }}>
                <h3>Upload CSV File</h3>
                <p>Expected columns:</p>
                <pre style={{
                    background: '#fff',
                    padding: '10px',
                    borderRadius: '4px',
                    marginBottom: '15px',
                }}>
                    {`- fiche_agence
- titre_affiche
- adresse
- design_points_of_sales
- photos_projet
- longitude
- latitude`}
                </pre>

                <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    disabled={processing}
                    style={{ marginBottom: '10px' }}
                />
            </div>

            {processing && (
                <div style={{
                    marginBottom: '20px',
                    padding: '10px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '4px',
                }}>
                    <p>Processing... {progress.current} of {progress.total}</p>
                    <div style={{
                        width: '100%',
                        height: '20px',
                        backgroundColor: '#fff',
                        borderRadius: '10px',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            width: `${(progress.current / progress.total) * 100}%`,
                            height: '100%',
                            backgroundColor: '#2196f3',
                            transition: 'width 0.3s ease',
                        }} />
                    </div>
                </div>
            )}

            {feedback && (
                <div style={{
                    padding: '10px',
                    marginBottom: '10px',
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32',
                    borderRadius: '4px',
                }}>
                    {feedback}
                </div>
            )}

            {error && (
                <div style={{
                    padding: '10px',
                    marginBottom: '10px',
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    borderRadius: '4px',
                }}>
                    {error}
                </div>
            )}
        </div>
    );
};

export default POSProcessor;